import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError, switchMap, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NewAuthService } from '../../service/refreshToken/new-auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private jwtHelper = new JwtHelperService();

  constructor(private router: Router, private newAuthService: NewAuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token check for MSAL / Microsoft endpoints to avoid interfering with auth flows
    if (req.url.includes('login.microsoftonline.com') || req.url.includes('graph.microsoft.com')) {
      return next.handle(req);
    }

    const token = localStorage.getItem('bearer_token');

    // No token means user is in login flow — don't interfere
    if (!token) {
      return next.handle(req);
    }

    // If token exists and is expired, try to silently regenerate before proceeding
    if (token && this.jwtHelper.isTokenExpired(token)) {
      return from(this.regenerateToken()).pipe(
        switchMap((newToken) => {
          const authReq = this.addAuthHeader(req, newToken);
          return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 401) {
                this.logoutUser();
              }
              return throwError(() => error);
            })
          );
        }),
        catchError(() => {
          // Token regeneration failed, logout the user
          this.logoutUser();
          return throwError(() => new Error('Token expired and regeneration failed. User has been logged out.'));
        })
      );
    }

    const authReq = token ? this.addAuthHeader(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // On 401, attempt token refresh before logging out
          return from(this.regenerateToken()).pipe(
            switchMap((refreshedToken) => {
              const retryReq = this.addAuthHeader(req, refreshedToken);
              return next.handle(retryReq);
            }),
            catchError(() => {
              this.logoutUser();
              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private async regenerateToken(): Promise<string> {
    return this.newAuthService.getAccessToken(['user.read']);
  }

  private logoutUser(): void {
    this.newAuthService.handleSessionExpiry();
  }
}
